import { useState, useEffect } from 'react';
import { useCurrency } from '../contexts/CurrencyContext';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from './ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './ui/alert-dialog';
import { toast } from 'sonner';
import {
  Trash2,
  AlertTriangle,
  Loader2,
  Ban,
  ShoppingCart,
  DollarSign,
  FileText,
} from 'lucide-react';

const VOID_REASONS = [
  'Customer cancelled order',
  'Wrong order entered',
  'Kitchen error - item unavailable',
  'Duplicate order',
  'Customer complaint',
  'Test order',
  'Other (specify below)',
];

export function VoidOrderModal({ 
  open, 
  onOpenChange, 
  order, 
  api, 
  onVoidSuccess 
}) {
  const { formatCurrency } = useCurrency();
  const [reason, setReason] = useState('');
  const [selectedPreset, setSelectedPreset] = useState('');
  const [customReason, setCustomReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [canVoid, setCanVoid] = useState(null);
  const [voidCheckReason, setVoidCheckReason] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);

  useEffect(() => {
    if (open && order?.id) {
      checkCanVoid();
    }
  }, [open, order?.id]);

  const checkCanVoid = async () => {
    try {
      const resp = await api.get(`/orders/${order.id}/can-void`);
      setCanVoid(resp.data.can_void);
      setVoidCheckReason(resp.data.reason || '');
    } catch (error) {
      setCanVoid(false);
      setVoidCheckReason('Failed to check void eligibility');
    }
  };

  const handlePresetSelect = (preset) => {
    setSelectedPreset(preset);
    if (preset !== 'Other (specify below)') {
      setReason(preset);
      setCustomReason('');
    } else {
      setReason('');
    }
  };

  const handleCustomReasonChange = (value) => {
    setCustomReason(value);
    if (selectedPreset === 'Other (specify below)') {
      setReason(value);
    }
  };

  const getFinalReason = () => {
    if (selectedPreset === 'Other (specify below)') {
      return customReason.trim();
    }
    return reason.trim();
  };

  const handleVoid = () => {
    const finalReason = getFinalReason();
    if (!finalReason || finalReason.length < 3) {
      toast.error('Please provide a valid reason (minimum 3 characters)');
      return;
    }
    setConfirmOpen(true);
  };

  const confirmVoid = async () => {
    setConfirmOpen(false);
    setSubmitting(true);
    
    try {
      const resp = await api.post(`/orders/${order.id}/void`, {
        reason: getFinalReason()
      });
      
      toast.success(`Order ${order.order_number} voided successfully`);
      onVoidSuccess?.(resp.data);
      onOpenChange(false);
      
      // Reset state
      setReason('');
      setSelectedPreset('');
      setCustomReason('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to void order');
    } finally {
      setSubmitting(false);
    }
  };

  if (!order) return null;

  const isPaid = order.status === 'COMPLETED' || order.status === 'PAID';

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-lg" data-testid="void-order-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-500">
              <Ban className="h-5 w-5" />
              Void Order
            </DialogTitle>
            <DialogDescription>
              This action cannot be undone. The order will be permanently marked as void.
            </DialogDescription>
          </DialogHeader>

          {/* Permission Check */}
          {canVoid === false && (
            <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-red-500">Cannot Void Order</p>
                <p className="text-sm text-muted-foreground">{voidCheckReason}</p>
              </div>
            </div>
          )}

          {canVoid !== false && (
            <div className="space-y-4 py-2">
              {/* Order Summary */}
              <div className="bg-muted rounded-lg p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <ShoppingCart className="h-4 w-4 text-muted-foreground" />
                    <span className="font-mono font-bold">{order.order_number}</span>
                  </div>
                  <Badge variant={isPaid ? 'default' : 'secondary'}>
                    {order.status}
                  </Badge>
                </div>
                
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <span className="text-muted-foreground">Type:</span>{' '}
                    <span className="font-medium">{order.order_type}</span>
                  </div>
                  {order.table_number && (
                    <div>
                      <span className="text-muted-foreground">Table:</span>{' '}
                      <span className="font-medium">{order.table_number}</span>
                    </div>
                  )}
                  <div>
                    <span className="text-muted-foreground">Items:</span>{' '}
                    <span className="font-medium">{order.items?.length || 0}</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Payment:</span>{' '}
                    <span className="font-medium">{isPaid ? 'PAID' : 'UNPAID'}</span>
                  </div>
                </div>

                <div className="border-t pt-2 flex items-center justify-between">
                  <div className="flex items-center gap-1 text-muted-foreground">
                    <DollarSign className="h-4 w-4" />
                    Total Amount
                  </div>
                  <span className="text-xl font-bold font-mono">
                    {formatCurrency(order.total)}
                  </span>
                </div>
              </div>

              {/* Warning for paid orders */}
              {isPaid && (
                <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3 flex items-start gap-2">
                  <AlertTriangle className="h-4 w-4 text-yellow-500 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-yellow-500">
                    This order has been paid. Voiding will mark the payment as refunded and affect your cash reconciliation.
                  </p>
                </div>
              )}

              {/* Reason Selection */}
              <div>
                <Label className="flex items-center gap-1">
                  <FileText className="h-4 w-4" />
                  Reason for Void *
                </Label>
                <div className="grid grid-cols-2 gap-2 mt-2">
                  {VOID_REASONS.map((preset) => (
                    <button
                      key={preset}
                      type="button"
                      onClick={() => handlePresetSelect(preset)}
                      className={`text-left text-sm p-2 rounded-md border transition-colors ${
                        selectedPreset === preset
                          ? 'border-primary bg-primary/10'
                          : 'border-border hover:bg-muted'
                      }`}
                    >
                      {preset}
                    </button>
                  ))}
                </div>
              </div>

              {/* Custom Reason */}
              {selectedPreset === 'Other (specify below)' && (
                <div>
                  <Label>Specify Reason *</Label>
                  <Textarea
                    value={customReason}
                    onChange={(e) => handleCustomReasonChange(e.target.value)}
                    placeholder="Enter the reason for voiding this order..."
                    className="mt-1"
                    rows={2}
                    data-testid="void-custom-reason"
                  />
                </div>
              )}

              {/* Selected Reason Display */}
              {getFinalReason() && (
                <div className="bg-muted/50 rounded-lg p-3 border">
                  <p className="text-sm text-muted-foreground">Selected reason:</p>
                  <p className="font-medium">{getFinalReason()}</p>
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleVoid}
              disabled={submitting || canVoid === false || !getFinalReason()}
              data-testid="void-order-btn"
            >
              {submitting ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Trash2 className="h-4 w-4 mr-2" />
              )}
              Void Order
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirmation Dialog */}
      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-red-500">
              <AlertTriangle className="h-5 w-5" />
              Confirm Void Order
            </AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to void order <strong>{order.order_number}</strong>?
              <br /><br />
              <strong>Amount:</strong> {formatCurrency(order.total)}
              <br />
              <strong>Reason:</strong> {getFinalReason()}
              <br /><br />
              This action will be permanently logged and cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmVoid}
              className="bg-red-500 hover:bg-red-600"
            >
              Yes, Void Order
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

// Hook to check void eligibility
export function useVoidCheck(api, orderId) {
  const [canVoid, setCanVoid] = useState(null);
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);

  const checkVoid = async () => {
    if (!orderId) return;
    setLoading(true);
    try {
      const resp = await api.get(`/orders/${orderId}/can-void`);
      setCanVoid(resp.data.can_void);
      setReason(resp.data.reason || '');
    } catch (error) {
      setCanVoid(false);
      setReason('Failed to check');
    } finally {
      setLoading(false);
    }
  };

  return { canVoid, reason, loading, checkVoid };
}

export default VoidOrderModal;
